import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  setOnlineStatus,
  updateLocation,
  getRiderActiveOrders,
  updateOrderStatus,
  completeStop,
  getRiderHistory,
  getRiderStats,
  raiseOrderIssue,
  requestVerificationOverride,
  getPendingOffer,
  acceptOffer,
  passOffer,
  getJobBoard,
  claimJob,
  decideRevokeRA,
  uploadFile,
  decidePaymentChangeRequest
} from "../../api/client";
import { listenForAssignments, listenForRiderEvents, listenForJobBoard } from "../../api/socket";
import RiderNav from "../../components/RiderNav";
import RiderMapNavigation from "../../components/RiderMapNavigation";
import PayoutAccountForm from "../../components/PayoutAccountForm";
import RiderProfileStatus from "../../components/RiderProfileStatus";
import ApplicationStatusCard from "../../components/ApplicationStatusCard";
import LoadingIndicator from "../../components/LoadingIndicator";
import CameraCaptureButton from "../../components/CameraCaptureButton";
import { PACKAGE_TYPE_LABELS } from "../../utils/packageLabels";

// Not a cancellation — a dispatcher can never cancel a request (only the
// sender/requester and Support Admin can). This is rule 2's "raise an
// issue" taxonomy instead; Support resolves it or cancels the order.
const ISSUE_REASONS = [
  { value: "package_size", label: "Package size/dimensions" },
  { value: "route_change", label: "Change in route/instructions" },
  { value: "prohibited_item", label: "Prohibited item" },
  { value: "others", label: "Other reason" },
];

const NEXT_STATUS = {
  accepted: "arrived_at_pickup",
  arrived_at_pickup: "picked_up",
  picked_up: "in_transit",
  in_transit: "delivered",
  delivered: "completed"
};

const STATUS_LABELS = {
  accepted: "Head to Pickup",
  arrived_at_pickup: "Arrived at Pickup",
  picked_up: "Package Picked Up",
  in_transit: "On the Way to Drop-off",
  delivered: "Delivered — Awaiting Completion",
  completed: "Completed"
};

const ACTION_LABELS = {
  accepted: "Mark Arrived at Pickup",
  arrived_at_pickup: "Mark Picked Up",
  picked_up: "Start Delivery (In Transit)",
  in_transit: "Mark Delivered",
  delivered: "Mark Completed"
};

export default function Dashboard() {
  const { account, updateAccount } = useAuth();
  const [activeTab, setActiveTab] = useState("active"); // "active" | "history" | "wallet"
  // A rider can have more than one active job at once (route optimization
  // along the same axis, up to MAX_ACTIVE_JOBS_PER_RIDER — enforced
  // server-side). `activeOrders` holds all of them; `order` below is
  // whichever one is currently expanded/focused in the detail view.
  const [activeOrders, setActiveOrders] = useState([]);
  const [focusedOrderId, setFocusedOrderId] = useState(null);
  const order = activeOrders.find((o) => o._id === focusedOrderId) || activeOrders[0] || null;
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  // Admin-configurable platform commission (see PricingSettings.commissionPercent
  // on the backend) — pulled from /riders/me/stats rather than hardcoded, so
  // this always matches what's actually deducted from a payout.
  const commissionPercent = stats?.commissionPercent ?? 15;
  const [driverLocation, setDriverLocation] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const watchIdRef = useRef(null);

  const [pinInput, setPinInput] = useState("");
  const [signatureInput, setSignatureInput] = useState("");
  const [raPhotoUrl, setRaPhotoUrl] = useState(null);
  const [raIdPhotoUrl, setRaIdPhotoUrl] = useState(null);
  const [raUploading, setRaUploading] = useState(false);

  // Support override for when the receiver's PIN genuinely can't be
  // produced — offered only after a real failed attempt, never up front.
  const [pinFailed, setPinFailed] = useState(false);
  const [overrideNote, setOverrideNote] = useState("");
  const [overrideBusy, setOverrideBusy] = useState(false);

  const [showCodConfirm, setShowCodConfirm] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [issueReason, setIssueReason] = useState("package_size");
  const [issueNote, setIssueNote] = useState("");
  const [issueBusy, setIssueBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [paymentChangeBusy, setPaymentChangeBusy] = useState(false);

  // Incoming offer — a job awaiting this rider's accept/pass, with a
  // countdown until it auto-expires to the next candidate.
  const [pendingOffer, setPendingOffer] = useState(null);
  const [offerSecondsLeft, setOfferSecondsLeft] = useState(0);
  const [offerBusy, setOfferBusy] = useState(false);

  // Job board — open, unclaimed requests this rider can browse and claim.
  const [jobBoard, setJobBoard] = useState([]);
  const [jobBoardLoading, setJobBoardLoading] = useState(false);
  const [claimBusyId, setClaimBusyId] = useState(null);


  const isApproved = account.approvalStatus === "approved";

  const loadStatsAndHistory = async () => {
    try {
      const [historyRes, statsRes] = await Promise.all([
        getRiderHistory(),
        getRiderStats()
      ]);
      setHistory(historyRes.data);
      setStats(statsRes.data);
    } catch {
      // ignore silent background fetch errors
    }
  };

  // Refetches the full active-job list from the server rather than patching
  // individual entries client-side — with up to MAX_ACTIVE_JOBS_PER_RIDER
  // jobs in flight at once, a full refetch is far less error-prone than
  // trying to keep several array entries in sync by hand.
  const loadActiveOrders = async () => {
    try {
      const { data } = await getRiderActiveOrders();
      setActiveOrders(data);
      setFocusedOrderId((prev) => (data.some((o) => o._id === prev) ? prev : (data[0]?._id || null)));
    } catch {
      // ignore silent background fetch errors
    }
  };

  useEffect(() => {
    if (!isApproved) return;
    loadActiveOrders();
    getPendingOffer().then(({ data }) => setPendingOffer(data)).catch(() => {});
    loadStatsAndHistory();
    loadJobBoard(); // pre-load so the tab badge count is accurate before it's clicked
    const unsubscribeAssignments = listenForAssignments(account._id, (assignedOrder) => {
      loadActiveOrders();
      setFocusedOrderId(assignedOrder._id);
      setPendingOffer(null);
    });
    const unsubscribeEvents = listenForRiderEvents(account._id, {
      onOffered: ({ order: offeredOrder }) => setPendingOffer(offeredOrder),
      // Both events are only emitted for changes triggered elsewhere (an
      // admin resolving an issue, a customer/admin cancelling) — the
      // rider's own actions already update state from the API response
      // directly, so a full refetch here is the simplest correct way to
      // reflect an externally-triggered change.
      onIssueUpdate: () => loadActiveOrders(),
      onStatusUpdate: ({ orderId, status }) => {
        if (["cancelled", "failed"].includes(status)) {
          setError(`A delivery (#${orderId.toString().slice(-6).toUpperCase()}) was ${status} by Support — it's no longer on your active dispatch.`);
        }
        loadActiveOrders();
      }
    });
    // Live nudge instead of requiring a manual refresh to see new job-board
    // postings (nobody was found nearby, or everyone passed/timed out).
    const unsubscribeJobBoard = listenForJobBoard(() => loadJobBoard());
    if (account.isOnline) startLocationSharing();
    return () => {
      unsubscribeAssignments();
      unsubscribeEvents();
      unsubscribeJobBoard();
    };
  }, [isApproved, account.isOnline]);

  // Local countdown for the pending offer — purely cosmetic urgency cue;
  // the server (schedulerService) is the source of truth for actual expiry.
  useEffect(() => {
    if (!pendingOffer?.offer?.expiresAt) {
      setOfferSecondsLeft(0);
      return;
    }
    const tick = () => {
      const secondsLeft = Math.max(0, Math.round((new Date(pendingOffer.offer.expiresAt) - Date.now()) / 1000));
      setOfferSecondsLeft(secondsLeft);
      if (secondsLeft === 0) {
        getPendingOffer().then(({ data }) => {
          setPendingOffer(data);
          if (!data) setError("That request timed out — it's been offered to another dispatcher, or opened up on the Job Board.");
        }).catch(() => {});
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [pendingOffer]);

  const startLocationSharing = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDriverLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        updateLocation(pos.coords.latitude, pos.coords.longitude).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true }
    );
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setDriverLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        updateLocation(pos.coords.latitude, pos.coords.longitude).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
  };

  const stopLocationSharing = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  };

  useEffect(() => () => stopLocationSharing(), []);

  const handleToggleOnline = async () => {
    setError("");
    setBusy(true);
    try {
      const nextOnline = !account.isOnline;
      const { data } = await setOnlineStatus(nextOnline);
      updateAccount(data);
      if (nextOnline) startLocationSharing();
      else stopLocationSharing();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update status");
    } finally {
      setBusy(false);
    }
  };

  const handleAdvanceStatus = async () => {
    if (!order) return;
    const next = NEXT_STATUS[order.status];
    if (!next) return;

    let verification;
    if (next === "delivered") {
      // PIN is always required, independent of Receiver Acknowledgement —
      // matches the backend, which never accepts "delivered" without it.
      // The one exception: an admin-approved verification override lets
      // this specific order through without a matching PIN.
      const overrideApproved = order.verificationOverride?.status === "approved";
      if (!overrideApproved && !pinInput) return setError("Enter the receiver's PIN to confirm delivery.");
      verification = pinInput.trim() ? { code: pinInput.trim() } : {};

      // RA's chosen method (if purchased) is captured as ADDITIONAL
      // evidence on top of the PIN, not instead of it.
      if (order.receiverAcknowledgement?.enabled) {
        const method = order.receiverAcknowledgement.method;
        if (method === "signature") {
          if (!signatureInput) return setError("Enter the receiver's name/signature to confirm delivery.");
          verification.signatureUrl = signatureInput;
        } else if (method === "photo") {
          if (!raPhotoUrl) return setError("Take a photo of the receiver to confirm delivery.");
          verification.photoUrl = raPhotoUrl;
        } else if (method === "id_photo") {
          if (!raIdPhotoUrl) return setError("Take a photo of the receiver's ID to confirm delivery.");
          verification.idPhotoUrl = raIdPhotoUrl;
        }
      }
    }

    setError("");
    setBusy(true);
    try {
      await updateOrderStatus(order._id, next, verification);
      await loadActiveOrders();
      setPinInput(""); setSignatureInput(""); setRaPhotoUrl(null); setRaIdPhotoUrl(null);
      setShowCodConfirm(false);
      setPinFailed(false);
      await loadStatsAndHistory();
    } catch (err) {
      const message = err.response?.data?.error || "Could not update order status";
      setError(message);
      if (next === "delivered" && /PIN is missing or incorrect/i.test(message)) {
        setPinFailed(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRequestOverride = async () => {
    if (!order) return;
    setError("");
    setOverrideBusy(true);
    try {
      await requestVerificationOverride(order._id, overrideNote);
      await loadActiveOrders();
      setOverrideNote("");
      setPinFailed(false);
    } catch (err) {
      setError(err.response?.data?.error || "Could not request a support override");
    } finally {
      setOverrideBusy(false);
    }
  };

  const handleRaUploadFile = async (file, target) => {
    if (!file) return;
    setRaUploading(true);
    setError("");
    try {
      const folder = target === "photo" ? "ra-photos" : "ra-ids";
      const { data } = await uploadFile(file, folder);
      if (target === "photo") setRaPhotoUrl(data.url); else setRaIdPhotoUrl(data.url);
    } catch (err) {
      setError(err.response?.data?.error || "Upload failed.");
    } finally {
      setRaUploading(false);
    }
  };

  const handleRaUpload = (e, target) => handleRaUploadFile(e.target.files?.[0], target);

  const handlePaymentChangeDecision = async (orderId, decision) => {
    setPaymentChangeBusy(true);
    setError("");
    try {
      await decidePaymentChangeRequest(orderId, decision);
      await loadActiveOrders();
    } catch (err) {
      setError(err.response?.data?.error || "Could not record your decision.");
    } finally {
      setPaymentChangeBusy(false);
    }
  };

  const handleRaiseIssue = async () => {
    setIssueBusy(true);
    setError("");
    try {
      await raiseOrderIssue(order._id, issueReason, issueNote);
      await loadActiveOrders();
      setShowIssueForm(false);
      setIssueNote("");
    } catch (err) {
      setError(err.response?.data?.error || "Could not raise issue.");
    } finally {
      setIssueBusy(false);
    }
  };

  const handleAcceptOffer = async () => {
    if (!pendingOffer) return;
    setOfferBusy(true);
    setError("");
    try {
      const { data } = await acceptOffer(pendingOffer._id);
      await loadActiveOrders();
      setFocusedOrderId(data._id);
      setPendingOffer(null);
    } catch (err) {
      setError(err.response?.data?.error || "This offer is no longer available.");
      setPendingOffer(null);
      getPendingOffer().then(({ data }) => setPendingOffer(data)).catch(() => {});
    } finally {
      setOfferBusy(false);
    }
  };

  const handlePassOffer = async () => {
    if (!pendingOffer) return;
    setOfferBusy(true);
    setError("");
    try {
      await passOffer(pendingOffer._id);
      setPendingOffer(null);
    } catch (err) {
      setError(err.response?.data?.error || "Could not pass this offer.");
    } finally {
      setOfferBusy(false);
    }
  };

  const loadJobBoard = async () => {
    setJobBoardLoading(true);
    try {
      const { data } = await getJobBoard();
      setJobBoard(data);
    } catch {
      // ignore silent background fetch errors
    } finally {
      setJobBoardLoading(false);
    }
  };

  const handleClaimJob = async (orderId) => {
    setClaimBusyId(orderId);
    setError("");
    try {
      const { data } = await claimJob(orderId);
      await loadActiveOrders();
      setFocusedOrderId(data._id);
      setActiveTab("active");
    } catch (err) {
      setError(err.response?.data?.error || "Could not claim this job — someone else may have already taken it.");
      loadJobBoard();
    } finally {
      setClaimBusyId(null);
    }
  };

  const handleRevokeDecision = async (approve) => {
    setRevokeBusy(true);
    setError("");
    try {
      await decideRevokeRA(order._id, approve);
      await loadActiveOrders();
    } catch (err) {
      setError(err.response?.data?.error || "Could not process revoke decision.");
    } finally {
      setRevokeBusy(false);
    }
  };

  const handleCompleteStop = async (stopIndex) => {
    setError("");
    setBusy(true);
    try {
      await completeStop(order._id, stopIndex);
      await loadActiveOrders();
    } catch (err) {
      setError(err.response?.data?.error || "Could not mark stop as delivered");
    } finally {
      setBusy(false);
    }
  };

  if (!isApproved) {
    const isRejected = account.approvalStatus === "rejected";
    return (
      <div className="rider-app">
        <RiderNav />
        <div className="page wide page-with-nav">
          <RiderProfileStatus />
          <div style={{ marginTop: "24px" }}>
            <ApplicationStatusCard
              status={isRejected ? "revoked" : "pending"}
              title={isRejected ? "Registration Rejected" : "Application Under Review"}
              description={isRejected
                ? "Your dispatcher registration was rejected by an administrator. Please contact support for more information."
                : "Your dispatcher registration is being reviewed by our admin team. You'll be able to go online once approved."}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rider-app">
      <RiderNav />
      <div className="page wide page-with-nav">

      <RiderProfileStatus showOnComplete />

      {/* ── Rider Portal Sub-Nav Tabs ── */}
      <div className="role-tabs" style={{ marginBottom: "20px" }}>
        <button
          className={`role-tab ${activeTab === "active" ? "active" : ""}`}
          onClick={() => setActiveTab("active")}
        >
          📦 Active Dispatch {activeOrders.length > 0 ? `(${activeOrders.length})` : ""}
        </button>
        <button
          className={`role-tab ${activeTab === "board" ? "active" : ""}`}
          onClick={() => { setActiveTab("board"); loadJobBoard(); }}
        >
          🗂️ Job Board {jobBoard.length ? `(${jobBoard.length})` : ""}
        </button>
        <button
          className={`role-tab ${activeTab === "wallet" ? "active" : ""}`}
          onClick={() => { setActiveTab("wallet"); loadStatsAndHistory(); }}
        >
          💰 Earnings &amp; Wallet
        </button>
        <button
          className={`role-tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => { setActiveTab("history"); loadStatsAndHistory(); }}
        >
          📜 Trip History ({history.length})
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {/* ── TAB 1: ACTIVE DISPATCH ── */}
      {activeTab === "active" && (
        <>
          {/* Online / Offline Switch */}
          <div className="online-toggle">
            <div>
              <span style={{ fontWeight: 600, display: "block" }}>
                {account.isOnline ? "You are Online" : "You are Offline"}
              </span>
              <span className="hint">
                {account.isOnline ? "Receiving nearby delivery orders in real-time" : "Toggle to start accepting orders"}
              </span>
            </div>
            <button
              type="button"
              className={`toggle-switch ${account.isOnline ? "on" : ""}`}
              onClick={handleToggleOnline}
              disabled={busy}
            />
          </div>

          {/* Incoming offer — accept or pass within the countdown window */}
          {pendingOffer && (
            <div className="quote-box" style={{ marginTop: "16px", borderColor: "rgba(250,84,28,0.4)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>📨 New Delivery Request</h3>
                <span className="status-badge warning">{offerSecondsLeft}s to respond</span>
              </div>
              <div className="route-line" style={{ marginTop: "12px" }}>
                <div className="route-dots">
                  <div className="route-dot"></div>
                  <div className="route-line-connector"></div>
                  <div className="route-dot destination"></div>
                </div>
                <div className="route-address">
                  <div><strong>Pickup:</strong> {pendingOffer.pickup.address}</div>
                  <div style={{ marginTop: "10px" }}><strong>Drop-off:</strong> {pendingOffer.dropoff.address}</div>
                </div>
              </div>
              {pendingOffer.vehicleType !== "truck" && (
                <div className="info-row" style={{ marginTop: "8px" }}>
                  <span className="label">Package</span>
                  <span className="value">
                    {PACKAGE_TYPE_LABELS[pendingOffer.packageType] || pendingOffer.packageType}
                    {pendingOffer.packageDescription ? ` — ${pendingOffer.packageDescription}` : ""}
                  </span>
                </div>
              )}
              <div className="price-row total">
                <span>Trip Fare Payout</span>
                <span>₦{pendingOffer.priceSnapshot?.total?.toLocaleString()}</span>
        
              </div>
             
              <div className="row" style={{ marginTop: "12px" }}>
                <button className="secondary" disabled={offerBusy} onClick={handlePassOffer}>
                  {offerBusy ? "…" : "Pass"}
                </button>
                {/* offerSecondsLeft is a cosmetic countdown only (see the
                    effect above) — it must never gate the button itself,
                    or a render that hasn't ticked yet leaves Accept
                    disabled on a genuinely still-acceptable offer. The
                    server is the real authority: if it really has expired,
                    acceptOffer() 409s and handleAcceptOffer already shows
                    that error. */}
                <button disabled={offerBusy} onClick={handleAcceptOffer}>
                  {offerBusy ? "…" : "Accept"}
                </button>
              </div>
            </div>
          )}

          {/* Other active jobs — a rider can have more than one at once
              (route optimization along the same axis, capped server-side at
              MAX_ACTIVE_JOBS_PER_RIDER). Only the focused one below shows
              full detail/status controls; tap any of these to switch focus. */}
          {activeOrders.length > 1 && (
            <div style={{ marginTop: "16px" }}>
              <h3>Your Active Jobs ({activeOrders.length})</h3>
              {activeOrders.map((o) => (
                <div
                  key={o._id}
                  className="rider-card"
                  style={{
                    marginBottom: "8px",
                    cursor: o._id === order?._id ? "default" : "pointer",
                    borderColor: o._id === order?._id ? "var(--brand-orange)" : undefined
                  }}
                  onClick={() => setFocusedOrderId(o._id)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <p style={{ fontSize: "13px" }}>
                        <strong>{o.pickup.address.slice(0, 28)}...</strong> → {o.dropoff.address.slice(0, 28)}...
                      </p>
                      <span className="status-badge info" style={{ marginTop: "4px", fontSize: "11px" }}>{STATUS_LABELS[o.status]}</span>
                    </div>
                    {o._id === order?._id ? (
                      <span className="hint">Viewing</span>
                    ) : (
                      <button style={{ width: "auto", marginTop: 0, padding: "6px 14px" }} onClick={(e) => { e.stopPropagation(); setFocusedOrderId(o._id); }}>
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {order ? (
            <>
              {/* Goboot GPS AI Navigation Module */}
              <RiderMapNavigation order={order} driverLocation={driverLocation} />

              <div className="delivery-card" style={{ marginTop: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h3>Active Delivery Details</h3>
                  <span className="status-badge info">{STATUS_LABELS[order.status]}</span>
                </div>

                {order.issue?.status === "open" && (
                  <div className="quote-box" style={{ marginBottom: "12px", background: "var(--warning-bg)", borderColor: "var(--warning-border)" }}>
                    <p style={{ fontWeight: 600, color: "var(--warning-hover)" }}>🚨 Issue reported — Support has been notified and will call the customer shortly.</p>
                  </div>
                )}

              <div className="info-row">
                <span className="label">Customer (Order Creator)</span>
                <span className="value">
                  {order.user?.name}
                  {order.user?.phone && (
                    <a href={`tel:${order.user.phone}`} style={{ marginLeft: "8px", color: "var(--text-accent)", textDecoration: "none", fontWeight: 700 }}>
                      📞 {order.user.phone}
                    </a>
                  )}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Pickup Address</span>
                <span className="value">{order.pickup.address}</span>
              </div>
              {order.pickupContact?.name && (
                <div className="info-row" style={{ background: "rgba(0,230,118,0.06)", padding: "6px 10px", borderRadius: "6px" }}>
                  <span className="label" style={{ color: "#00E676", fontWeight: 700 }}>👤 Pickup Contact</span>
                  <span className="value">
                    <strong>{order.pickupContact.name}</strong>
                    {order.pickupContact.phone && (
                      <a href={`tel:${order.pickupContact.phone}`} style={{ marginLeft: "8px", color: "#00E676", textDecoration: "none", fontWeight: 700 }}>
                        📞 Call {order.pickupContact.phone}
                      </a>
                    )}
                  </span>
                </div>
              )}
              <div className="info-row">
                <span className="label">Drop-off Address</span>
                <span className="value">{order.dropoff.address}</span>
              </div>
              {order.dropoffContact?.name && (
                <div className="info-row" style={{ background: "rgba(56,189,248,0.06)", padding: "6px 10px", borderRadius: "6px" }}>
                  <span className="label" style={{ color: "#38bdf8", fontWeight: 700 }}>🏁 Drop-off Contact</span>
                  <span className="value">
                    <strong>{order.dropoffContact.name}</strong>
                    {order.dropoffContact.phone && (
                      <a href={`tel:${order.dropoffContact.phone}`} style={{ marginLeft: "8px", color: "#38bdf8", textDecoration: "none", fontWeight: 700 }}>
                        📞 Call {order.dropoffContact.phone}
                      </a>
                    )}
                  </span>
                </div>
              )}
              {/* Tells the dispatcher exactly what (if anything) to collect
                  from the receiver — a B2B/Corporate order is already billed
                  to the company account, cash needs collecting, and anything
                  else shows its REAL payment status rather than assuming any
                  non-cash order is automatically paid (it might not be yet). */}
              <div className="info-row">
                <span className="label">Payment</span>
                <span className="value">
                  {order.paymentMethod === "b2b_invoice" ? (
                    <span className="status-badge info">📋 Paid by B2B/Corporate — do not collect</span>
                  ) : order.paymentMethod === "cash" ? (
                    <span className="status-badge warning">💵 Cash on Delivery — collect ₦{order.priceSnapshot?.total?.toLocaleString()}</span>
                  ) : order.paymentMethod === "receiver_pay" && order.paymentStatus === "paid" ? (
                    <span className="status-badge success">✅ Paid by Receiver — do not collect</span>
                  ) : order.paymentMethod === "receiver_pay" ? (
                    <span className="status-badge danger">⚠️ Receiver pays online via their link — do not collect cash</span>
                  ) : order.paymentStatus === "paid" ? (
                    <span className="status-badge success">✅ Paid — do not collect</span>
                  ) : (
                    <span className="status-badge danger">⚠️ Not Paid</span>
                  )}
                </span>
              </div>
              {order.paymentMethodChangeRequest?.status === "pending" && (
                <div className="info-row" style={{ background: "var(--warning-bg)", padding: "8px 10px", borderRadius: "6px", flexDirection: "column", alignItems: "flex-start" }}>
                  <span style={{ fontWeight: 700, color: "var(--warning-hover)" }}>
                    💳 The receiver asked to switch to Cash on Delivery
                  </span>
                  <p className="hint" style={{ margin: "4px 0 8px" }}>
                    Approve only if you're okay collecting cash instead of the guaranteed online payment.
                  </p>
                  <div className="row" style={{ width: "100%" }}>
                    <button
                      className="secondary"
                      style={{ flex: 1, marginTop: 0 }}
                      disabled={paymentChangeBusy}
                      onClick={() => handlePaymentChangeDecision(order._id, "deny")}
                    >
                      ✕ Deny
                    </button>
                    <button
                      style={{ flex: 1, marginTop: 0 }}
                      disabled={paymentChangeBusy}
                      onClick={() => handlePaymentChangeDecision(order._id, "approve")}
                    >
                      ✓ Approve
                    </button>
                  </div>
                </div>
              )}
              <div className="info-row">
                <span className="label">Package Size</span>
                <span className="value">{PACKAGE_TYPE_LABELS[order.packageType] || order.packageType}</span>
              </div>
              {order.vehicleType === "truck" ? (
                (order.packageDescription || order.cargoWeight) && (
                  <div className="info-row">
                    <span className="label">Cargo</span>
                    <span className="value">{order.packageDescription || "-"}{order.cargoWeight ? ` · ${order.cargoWeight}` : ""}</span>
                  </div>
                )
              ) : (
                <>
                  {order.packageDescription && (
                    <div className="info-row">
                      <span className="label">Package Description</span>
                      <span className="value">{order.packageDescription}</span>
                    </div>
                  )}
                </>
              )}

              <div className="price-breakdown">
                <div className="price-row total">
                  <span>Trip Fare Payout</span>
                  <span>₦{order.priceSnapshot.total.toLocaleString()}</span>
                </div>
                <div className="price-row total">
                  <span>Service Fee ({commissionPercent}%)</span>
                  <span>₦{Math.round(order.priceSnapshot.total * (commissionPercent / 100)).toLocaleString()}</span>
                </div>
                <div className="price-row total">
                  <span>Your earnings</span>
                  <span>₦{(order.priceSnapshot.total - Math.round(order.priceSnapshot.total * (commissionPercent / 100))).toLocaleString()}</span>
                </div>
              </div>

              {/* Multi-stop progress */}
              {order.stops && order.stops.length > 0 && (
                <>
                  <h3 style={{ marginTop: "12px" }}>Stops ({order.stops.filter(s => s.status === "delivered").length}/{order.stops.length} done)</h3>
                  {order.stops.map((stop, idx) => (
                    <div key={idx} className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", borderBottom: "1px solid var(--border)", padding: "8px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <div>
                          <span style={{ color: stop.status === "delivered" ? "var(--text-accent)" : "var(--text-primary)", fontWeight: 600 }}>
                            {stop.status === "delivered" ? "✓ " : ""}{stop.address}
                          </span>
                        </div>
                        {stop.status !== "delivered" && order.status === "in_transit" && (
                          <button
                            style={{ width: "auto", padding: "4px 10px", fontSize: "12px", marginTop: 0 }}
                            disabled={busy || order.stops.findIndex(s => s.status === "pending") !== idx}
                            onClick={() => handleCompleteStop(idx)}>
                            Mark Delivered
                          </button>
                        )}
                        {stop.status === "delivered" && (
                          <span className="status-badge" style={{ fontSize: "11px", padding: "3px 8px" }}>Done</span>
                        )}
                      </div>
                      {stop.instructions && (
                        <p className="hint" style={{ marginTop: "4px" }}>📝 {stop.instructions}</p>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* RA revoke request awaiting this rider's decision */}
              {order.receiverAcknowledgement?.revokeStatus === "requested" && (
                <div className="quote-box" style={{ marginTop: "12px", background: "rgba(250,84,28,0.08)" }}>
                  <p style={{ fontWeight: 600 }}>⚠️ Sender requested to revoke Receiver Acknowledgement</p>
                  <div className="row" style={{ marginTop: "8px" }}>
                    <button className="secondary" disabled={revokeBusy} onClick={() => handleRevokeDecision(false)}>Deny</button>
                    <button disabled={revokeBusy} onClick={() => handleRevokeDecision(true)}>Approve</button>
                  </div>
                </div>
              )}

              {/* Receiver verification, required before "Mark Delivered". The
                  PIN is always required — every order gets one for exactly
                  this reason. If Receiver Acknowledgement is enabled, its
                  chosen method is captured as ADDITIONAL evidence on top of
                  the PIN, never a substitute for it. */}
              {order.status === "in_transit" && (
                <div className="quote-box" style={{ marginTop: "12px" }}>
                  <h3>Receiver Verification Required</h3>
                  <input
                    type="text"
                    placeholder="Enter receiver's PIN"
                    value={pinInput}
                    onChange={e => setPinInput(e.target.value)}
                    style={{ marginTop: "8px" }}
                  />

                  {/* Support override — offered only after a real failed PIN
                      attempt, never up front. The receiver may genuinely be
                      unable to produce the PIN (lost the SMS, wrong number
                      on file, etc.); Support calls to confirm before letting
                      the order through without it. */}
                  {order.verificationOverride?.status === "requested" && (
                    <p className="hint" style={{ marginTop: "8px", color: "var(--warning-hover)" }}>
                      ⏳ Support override requested — waiting on admin review.
                    </p>
                  )}
                  {order.verificationOverride?.status === "approved" && (
                    <p className="hint" style={{ marginTop: "8px", color: "var(--success-hover, #2e7d32)" }}>
                      ✅ Support approved a one-time override for this order — you can mark it delivered without the PIN.
                    </p>
                  )}
                  {order.verificationOverride?.status === "denied" && (
                    <p className="error" style={{ marginTop: "8px" }}>
                      ❌ Support denied the override request{order.verificationOverride.decisionNote ? `: ${order.verificationOverride.decisionNote}` : "."} Ask the receiver for the correct PIN.
                    </p>
                  )}
                  {pinFailed && (!order.verificationOverride || order.verificationOverride.status === "none") && (
                    <div style={{ marginTop: "8px" }}>
                      <p className="hint" style={{ marginBottom: "6px" }}>
                        PIN not working? If the receiver genuinely can't produce it, request a support override instead of retrying.
                      </p>
                      <input
                        type="text"
                        placeholder="Note for Support (optional) — why can't the receiver provide the PIN?"
                        value={overrideNote}
                        onChange={e => setOverrideNote(e.target.value)}
                        style={{ marginBottom: "8px" }}
                      />
                      <button className="secondary" disabled={overrideBusy} onClick={handleRequestOverride}>
                        {overrideBusy ? "Requesting..." : "🆘 Request Support Override"}
                      </button>
                    </div>
                  )}

                  {order.receiverAcknowledgement?.enabled && order.receiverAcknowledgement.method === "signature" && (
                    <input
                      type="text"
                      placeholder="Receiver's full name (signature)"
                      value={signatureInput}
                      onChange={e => setSignatureInput(e.target.value)}
                      style={{ marginTop: "8px" }}
                    />
                  )}
                  {order.receiverAcknowledgement?.enabled && order.receiverAcknowledgement.method === "photo" && (
                    <div style={{ marginTop: "8px" }}>
                      {raPhotoUrl && <p className="hint" style={{ marginBottom: "6px" }}>📷 Photo captured — retake below if needed</p>}
                      <div className="row">
                        <CameraCaptureButton
                          label="📷 Take Photo"
                          busyLabel="Uploading..."
                          busy={raUploading}
                          onCapture={(file) => handleRaUploadFile(file, "photo")}
                        />
                        <label className="location-btn" style={{ display: "inline-flex", justifyContent: "center", cursor: "pointer", flex: 1 }}>
                          {raUploading ? "Uploading..." : "🖼️ Choose from Device"}
                          <input type="file" accept="image/*" style={{ display: "none" }} disabled={raUploading} onChange={e => handleRaUpload(e, "photo")} />
                        </label>
                      </div>
                    </div>
                  )}
                  {order.receiverAcknowledgement?.enabled && order.receiverAcknowledgement.method === "id_photo" && (
                    <div style={{ marginTop: "8px" }}>
                      {raIdPhotoUrl && <p className="hint" style={{ marginBottom: "6px" }}>🪪 ID captured — retake below if needed</p>}
                      <div className="row">
                        <CameraCaptureButton
                          label="📷 Take Photo of ID"
                          busyLabel="Uploading..."
                          busy={raUploading}
                          onCapture={(file) => handleRaUploadFile(file, "id_photo")}
                        />
                        <label className="location-btn" style={{ display: "inline-flex", justifyContent: "center", cursor: "pointer", flex: 1 }}>
                          {raUploading ? "Uploading..." : "🖼️ Choose from Device"}
                          <input type="file" accept="image/*" style={{ display: "none" }} disabled={raUploading} onChange={e => handleRaUpload(e, "id_photo")} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cash orders need an explicit "yes I collected it" confirmation
                  before the final completion step — this is the only point in
                  the flow where cash actually changes hands. */}
              {order.status === "delivered" && order.paymentMethod === "cash" && !showCodConfirm && (
                <button onClick={() => setShowCodConfirm(true)} style={{ marginTop: "16px" }}>
                  {ACTION_LABELS[order.status]}
                </button>
              )}
              {order.status === "delivered" && order.paymentMethod === "cash" && showCodConfirm && (
                <div className="quote-box" style={{ marginTop: "16px", background: "var(--success-bg)", borderColor: "var(--success-border)" }}>
                  <p style={{ fontWeight: 600 }}>
                    Confirm you've collected <strong>₦{order.priceSnapshot.total.toLocaleString()}</strong> in cash from the customer.
                  </p>
                  <div className="row" style={{ marginTop: "8px" }}>
                    <button className="secondary" disabled={busy} onClick={() => setShowCodConfirm(false)}>Not Yet</button>
                    <button className="success" disabled={busy} onClick={handleAdvanceStatus}>
                      {busy ? "Completing..." : "✓ Yes, Cash Collected"}
                    </button>
                  </div>
                </div>
              )}

              {NEXT_STATUS[order.status] && !(order.status === "delivered" && order.paymentMethod === "cash") && (
                <button
                  disabled={busy || (
                    order.status === "in_transit" && (
                      (!pinInput && order.verificationOverride?.status !== "approved") ||
                      (order.receiverAcknowledgement?.enabled && order.receiverAcknowledgement.method === "signature" && !signatureInput) ||
                      (order.receiverAcknowledgement?.enabled && order.receiverAcknowledgement.method === "photo" && !raPhotoUrl) ||
                      (order.receiverAcknowledgement?.enabled && order.receiverAcknowledgement.method === "id_photo" && !raIdPhotoUrl)
                    )
                  )}
                  onClick={handleAdvanceStatus}
                  style={{ marginTop: "16px" }}
                >
                  {busy ? "Updating..." : ACTION_LABELS[order.status]}
                </button>
              )}

              {/* Raise an issue — dispatchers can't cancel a request (rule 1); only
                  Support Admin or the sender can. Raising an issue notifies Support,
                  who resolves it or cancels the order from their end. */}
              {!["delivered", "completed"].includes(order.status) && order.issue?.status !== "open" && (
                <div style={{ marginTop: "12px" }}>
                  {!showIssueForm ? (
                    <button className="secondary" style={{ color: "var(--warning-hover)" }} onClick={() => setShowIssueForm(true)}>
                      🚨 Raise an Issue
                    </button>
                  ) : (
                    <div className="quote-box">
                      <h3>Raise an Issue</h3>
                      <p className="hint" style={{ marginBottom: "8px" }}>
                        Use this when delivery details/expectations can't be fulfilled (e.g. a prohibited item). This does not cancel the request — Support will call the customer and resolve it.
                      </p>
                      <div className="form-field">
                        <label>Reason</label>
                        <select value={issueReason} onChange={e => setIssueReason(e.target.value)}>
                          {ISSUE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </div>
                      <textarea
                        placeholder="Additional details (optional)"
                        value={issueNote}
                        onChange={e => setIssueNote(e.target.value)}
                        rows={2}
                        style={{ marginBottom: "8px", resize: "vertical" }}
                      />
                      <div className="row">
                        <button className="secondary" onClick={() => setShowIssueForm(false)} disabled={issueBusy}>Never Mind</button>
                        <button style={{ background: "var(--warning)" }} disabled={issueBusy} onClick={handleRaiseIssue}>
                          {issueBusy ? "Reporting..." : "Report Issue"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
          ) : (
            account.isOnline && !pendingOffer && (
              <div className="empty-state" style={{ marginTop: "24px" }}>
                <span className="empty-state-icon">📡</span>
                <p className="empty-state-text waiting-dots">Waiting for delivery requests</p>
                <p className="hint" style={{ marginTop: "8px" }}>
                  Nothing offered yet? Check the <strong>Job Board</strong> tab for open requests you can claim.
                </p>
              </div>
            )
          )}
        </>
      )}

      {/* ── TAB 2: EARNINGS & WALLET ── */}
      {activeTab === "wallet" && (
        <div style={{ animation: "fadeInUp 0.3s ease" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
            <div className="quick-card">
              <span className="quick-card-icon">💰</span>
              <span className="quick-card-title">Total Earnings</span>
              <strong style={{ fontSize: "20px", color: "var(--text-accent)" }}>
                ₦{(stats?.totalEarnings || 0).toLocaleString()}
              </strong>
            </div>

            <div className="quick-card">
              <span className="quick-card-icon">📦</span>
              <span className="quick-card-title">Completed Trips</span>
              <strong style={{ fontSize: "20px", color: "var(--text-primary)" }}>
                {stats?.totalCompleted || 0}
              </strong>
            </div>

            <div className="quick-card">
              <span className="quick-card-icon">💵</span>
              <span className="quick-card-title">COD Collected</span>
              <strong style={{ fontSize: "20px", color: stats?.codPending > 0 ? "var(--warning)" : "var(--text-primary)" }}>
                ₦{(stats?.codPending || 0).toLocaleString()}
              </strong>
            </div>
          </div>

          <div className="rider-card">
            <h3>Dispatcher Account Information</h3>
            <div className="info-row" style={{ marginTop: "12px" }}>
              <span className="label">Vehicle Registered</span>
              <span className="value">
                {stats?.vehicleType === "van" ? "🚐 Cargo Van" : stats?.vehicleType === "truck" ? "🚚 Delivery Truck" : "🏍️ Motorcycle"}
                {stats?.vehiclePlateNumber ? ` (${stats.vehiclePlateNumber})` : ""}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Operating City</span>
              <span className="value">{stats?.city || "Lagos"}</span>
            </div>
            <div className="info-row">
              <span className="label">Dispatcher Rating</span>
              <span className="value">⭐ {(stats?.rating || 5).toFixed(1)} / 5.0</span>
            </div>
            {stats?.priorityStatus === "priority" && (
              <div className="info-row">
                <span className="label">Dispatch Priority</span>
                <span className="value">⚡ Priority Dispatcher</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Your Referral Code</span>
              <span className="value">{stats?.referralCode || "-"} <span className="hint">(share to earn ₦1,000 when they're approved)</span></span>
            </div>
          </div>

          {/* PAYOUT ACCOUNT SETUP — shared with the Wallet page's Withdraw
              button, so there's one place to manage this, not two. */}
          <div style={{ marginTop: "16px" }}>
            <PayoutAccountForm
              payoutAccount={stats?.payoutAccount}
              onUpdated={(payoutAccount) => setStats((s) => ({ ...s, payoutAccount }))}
            />
          </div>
        </div>
      )}

      {/* ── TAB 3: TRIP HISTORY ── */}
      {activeTab === "history" && (
        <div style={{ animation: "fadeInUp 0.3s ease" }}>
          {history.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-icon">📜</span>
              <p className="empty-state-text">No delivery history yet.</p>
            </div>
          ) : (
            history.map((h) => (
              <div key={h._id} className="history-row" style={{ marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <strong>Order #{h._id.slice(-6).toUpperCase()}</strong>
                    <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                      {new Date(h.updatedAt).toLocaleString()}
                    </p>
                    <p style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>
                      📍 {h.pickup.address.slice(0, 30)}... → {h.dropoff.address.slice(0, 30)}...
                    </p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <strong style={{ fontSize: "16px", color: "var(--text-accent)", display: "block" }}>
                      +₦{h.priceSnapshot?.total?.toLocaleString()}
                    </strong>
                    <span className={`status-badge ${h.status === "completed" || h.status === "delivered" ? "info" : "danger"}`}>
                      {h.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── TAB: JOB BOARD ── */}
      {activeTab === "board" && (
        <div style={{ animation: "fadeInUp 0.3s ease" }}>
          <p className="hint" style={{ marginBottom: "12px" }}>
            Open requests nobody nearby has claimed yet, matching your vehicle category. First to claim gets it.
          </p>
          {jobBoardLoading ? (
            <LoadingIndicator label="Loading job board..." compact />
          ) : jobBoard.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-icon">🗂️</span>
              <p className="empty-state-text">No open requests right now — check back shortly.</p>
            </div>
          ) : (
            jobBoard.map((j) => (
              <div key={j._id} className="delivery-card">
                <div className="route-line">
                  <div className="route-dots">
                    <div className="route-dot"></div>
                    <div className="route-line-connector"></div>
                    <div className="route-dot destination"></div>
                  </div>
                  <div className="route-address">
                    <div><strong>Pickup:</strong> {j.pickup.address}</div>
                    <div style={{ marginTop: "10px" }}><strong>Drop-off:</strong> {j.dropoff.address}</div>
                  </div>
                </div>
                {j.vehicleType !== "truck" && (
                  <div className="info-row" style={{ marginTop: "8px" }}>
                    <span className="label">Package</span>
                    <span className="value">
                      {PACKAGE_TYPE_LABELS[j.packageType] || j.packageType}
                      {j.packageDescription ? ` — ${j.packageDescription}` : ""}
                    </span>
                  </div>
                )}
                <div className="price-row total">
                  <span>Trip Fare Payout</span>
                  <span>₦{j.priceSnapshot?.total?.toLocaleString()}</span>
                </div>
                <button disabled={claimBusyId === j._id} onClick={() => handleClaimJob(j._id)} style={{ marginTop: "8px" }}>
                  {claimBusyId === j._id ? "Claiming..." : "Claim Job"}
                </button>
              </div>
            ))
          )}
        </div>
      )}
      </div>
    </div>
  );
}
